import React, { useState } from 'react';
import { registerWithEmail, createUserDocument } from '../../services/auth';
import { User } from 'firebase/auth';
import { LANDING_TERMS_URL, LANDING_PRIVACY_URL } from '../../constants/urls';
import AuthPageShell, { AuthFormHeading, AuthRule, AuthCrossLink } from './AuthPageShell';
import GoogleSignInButton from './GoogleSignInButton';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface RegisterFormProps {
    onSwitchToLogin: () => void;
}

function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const userCredential = await registerWithEmail(email, password);
            await createUserDocument(userCredential.user as User);
            // The verify-email gate (and the initial verification send) is handled
            // by AuthGate once the auth state listener picks up the new user.
        } catch (err: any) {
            setError(err.message || 'Failed to register');
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell>
            <AuthFormHeading title="Create Account" />

            <form onSubmit={handleSubmit} className="flex flex-col">
                <Input
                    variant="line"
                    type="email"
                    placeholder="Email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                />
                <Input
                    variant="line"
                    type="password"
                    placeholder="Password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                />
                <Input
                    variant="line"
                    type="password"
                    placeholder="Confirm Password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                />

                {error && <p className="mt-4 text-meta font-semibold text-alert">{error}</p>}

                <Button type="submit" variant="primary" size="lg" fullWidth className="mt-6" disabled={isLoading}>
                    {isLoading ? 'Creating account…' : 'Create account'}
                </Button>
            </form>

            <AuthRule className="mt-7" />

            <div className="mt-6">
                <GoogleSignInButton onError={setError} disabled={isLoading} label="Sign up with Google" />
            </div>

            <AuthRule className="mt-7" />

            <AuthCrossLink prompt="Already have an account?" label="Log in" onClick={onSwitchToLogin} />

            <p className="mt-5 text-meta leading-relaxed text-subtle">
                By creating an account you agree to the{' '}
                <a href={LANDING_TERMS_URL} className="text-ink-secondary">Terms of Use</a> and{' '}
                <a href={LANDING_PRIVACY_URL} className="text-ink-secondary">Privacy Policy</a>.
            </p>
        </AuthPageShell>
    );
}

export default RegisterForm;
